import React, { useEffect, useMemo, useState } from 'react'
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_PUB_KEY } from '../../../constants';
import AdminBillingForm from './AdminBillingForm';
import { getBillingInfos } from '../../../services/Billing/Billing.service';
import { useTranslation } from 'react-i18next';
import { Message, Icon, Grid, Header, Segment, Divider, Form } from 'semantic-ui-react'
import {UltyInputText} from '@ulty/ulty-ui';
import AdminBillingInvoices from './AdminBillingInvoices';
import useUser from '../../Shared/UserProvider/useUser';
import slugify from 'slugify';
import { useQuery, useQueryClient } from '@tanstack/react-query'
import AdvancedFilters from '../../Home/AdvancedFilters'
import { isProviderACompany, isProviderAPos } from '../../../services/Provider/Provider.service'
import useBreadcrumb from '../../NavBar/useBreadcrumb'



const stripeFactory = () => {
    let stripePromise = null;
    return () => {
        if(!stripePromise){
            stripePromise = loadStripe(STRIPE_PUB_KEY);
        }
        return stripePromise;
    }
}

const stripeLoader = stripeFactory();

const AdminBilling = () => {
    const [t] = useTranslation();
    const { user } = useUser()
    const queryClient = useQueryClient()
    const [selectedPosId, setSelectedPosId] = useState((isProviderACompany(user.provider) || user.isLoggedInOwnScope) ?
      user.loginContext.providers.filter(isProviderAPos)[0]?.object.id :
      user.provider.object.id)
    const {setPaths} = useBreadcrumb()

    useEffect(() => {
        setPaths([{
            text: t('breadcrumb.home'),
            link: true,
            path: '/',
        }, {
            text: t('breadcrumb.settings'),
            link: false,
            path: '/settings',
        }])
    }, [setPaths, t])

    const stripePromise = useMemo(stripeLoader,[]);

    const handleSelectedPosChange = (filters) => {
        queryClient.setQueryData(['pointofsales', filters.merchantId], filters.merchantId)
        setSelectedPosId(filters.merchantId)
    }

    const providerId = user.isLoggedInOwnScope ? user.providers
      .find(provider => provider.object.id === selectedPosId)?.id : user?.provider?.id

    const { data: billingDetails, isLoading } = useQuery({
        queryKey: ['billingDetails', providerId],
        queryFn: () => getBillingInfos(providerId),
        enabled: !!providerId,
    })

    return (
      <Form>
          {user.isLoggedInOwnScope && (
            <>
                <AdvancedFilters
                  filters={{
                      merchantId: selectedPosId,
                  }}
                  debounceTimeInMs={0}
                  onFiltersChange={handleSelectedPosChange}
                >
                    <AdvancedFilters.PosFilter/>
                </AdvancedFilters>
                <Divider/>
            </>
          )}

          <Segment>
              {isLoading && (
                <Message icon>
                    <Icon name='circle notched' loading />
                    <Message.Content>
                        <Message.Header>{t('global.loading')}</Message.Header>
                        {t('administration.billing.loading')}
                    </Message.Content>
                </Message>
              )}

              {!isLoading && !billingDetails?.id && (
                <Message icon>
                    <Icon name='question' />
                    <Message.Content>
                        {t('administration.billing.no_billing_details')}
                    </Message.Content>
                </Message>
              )}

              {billingDetails?.id && (
                <>
                    <Grid>
                        <Grid.Row>
                            <Grid.Column width={16}>
                                <Header className="form-header" as='h3' content={t('administration.billing.infos_header')} />
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column width={8}>
                                {user.isStaff && billingDetails.stripeCustomerId ? ( <UltyInputText
                                  readonly={true}
                                  label={`${t('administration.billing.name')}:`}>
                                    <a target='_blank' rel="noreferrer" href={`https://dashboard.stripe.com/customers/${billingDetails.stripeCustomerId}`}>{billingDetails.name}</a>
                                </UltyInputText> ) : (
                                  <UltyInputText
                                    readonly={true}
                                    label={`${t('administration.billing.name')}:`}
                                    value={billingDetails.name}
                                  />
                                )}

                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column width={8}>
                                <UltyInputText
                                  readonly={true}
                                  label={`${t('administration.billing.address')}:`}
                                  value={billingDetails.address}
                                />
                            </Grid.Column>
                            <Grid.Column width={4}>
                                <UltyInputText
                                  readonly={true}
                                  label={`${t('administration.billing.postalCode')}:`}
                                  value={billingDetails.postalCode}
                                />
                            </Grid.Column>
                            <Grid.Column width={4}>
                                <UltyInputText
                                  readonly={true}
                                  label={`${t('administration.billing.city')}:`}
                                  value={billingDetails.city}
                                />
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column width={8}>
                                <UltyInputText
                                  readonly={true}
                                  label={`${t('administration.billing.siret')}:`}
                                  value={billingDetails.siret}
                                />
                            </Grid.Column>
                            <Grid.Column width={8}>
                                <UltyInputText
                                  readonly={true}
                                  label={`${t('administration.billing.vatNumber')}:`}
                                  value={billingDetails.vatNumber}
                                />
                            </Grid.Column>
                        </Grid.Row>
                        { billingDetails.mandateUrl && (
                          <Grid.Row>
                              <Grid.Column width={8}>
                                  <UltyInputText
                                    readonly={true}
                                    label={`${t('administration.billing.mandate')}:`}
                                    value={(<a href={billingDetails.mandateUrl} target="_blank" rel="noopener noreferrer">{t('administration.billing.mandate_download')}</a>)}
                                  />
                              </Grid.Column>
                          </Grid.Row>
                        )}
                        { billingDetails.billingNote && (
                          <Grid.Row>
                              <Grid.Column width={8}>
                                  { billingDetails.billingNote.split(/\n/g).map((note, idx) => (
                                    <UltyInputText
                                      key={`${slugify(note)}-${idx}`}
                                      readonly={true}
                                      label={idx === 0 ? t('administration.billing.conditions') : ''}
                                      value={(<span>&bull;&nbsp;&nbsp;{note}</span>)}
                                    />
                                  ))}
                              </Grid.Column>
                          </Grid.Row>
                        )}
                        <Grid.Row>
                            <Grid.Column width={16}>
                                <Elements stripe={stripePromise}>
                                    <AdminBillingForm providerId={providerId} billingDetails={billingDetails} />
                                </Elements>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column width={16}>
                                <Header className="form-header" as='h3' content={t('administration.billing.invoices_header')} />
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column width={16}>
                                <AdminBillingInvoices invoices={billingDetails.invoices||[]} />
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </>
              )}
          </Segment>
      </Form>
    );
}

export default AdminBilling;
